import { CartAddEvent, CartErrorEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

class RelevantProductAtc extends HTMLElement {
  connectedCallback() {
    this.button?.addEventListener('click', this.handleClick);
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.handleClick);
  }

  get button() {
    return this.querySelector('[ref="addToCartButton"]');
  }

  get errorElement() {
    return this.querySelector('[ref="addToCartTextError"]');
  }

  get liveRegion() {
    return this.querySelector('[ref="liveRegion"]');
  }

  get errorMessage() {
    return this.querySelector('[ref="addToCartErrorMessage"]');
  }

  handleClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const button = this.button;
    const variantId = this.dataset.variantId;
    const quantity = Number(this.dataset.quantity) || 1;

    if (!button || button.disabled || !variantId) return;

    this.setLoading(true);
    this.hideError();

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', quantity.toString());

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((item) => item instanceof HTMLElement && item.dataset.sectionId)
      .filter(Boolean);

    if (sectionIds.length > 0) {
      formData.set('sections', sectionIds.join(','));
      formData.set('sections_url', window.location.pathname);
    }

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('javascript', { body: formData }),
        headers: {
          ...fetchConfig('javascript').headers,
          Accept: 'text/html',
        },
      });
      const addResult = await addResponse.json();

      if (addResult.status) {
        this.showError(addResult.message);
        this.dispatchEvent(
          new CartErrorEvent(this.id, addResult.message, addResult.description, addResult.errors)
        );
        return;
      }

      const cart = await this.fetchCart();
      this.announce(Theme.translations.added);
      this.dispatchEvent(
        new CartAddEvent(cart ?? undefined, variantId, {
          source: 'relevant-product-card',
          itemCount: cart?.item_count ?? quantity,
          productId: this.dataset.productId,
          variantId,
          sections: addResult.sections,
        })
      );
    } catch (error) {
      this.showError(Theme.translations?.error || 'Error');
      console.error(error);
    } finally {
      this.setLoading(false);
    }
  };

  async fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (_) {
      return null;
    }
  }

  setLoading(isLoading) {
    const button = this.button;
    if (!button) return;

    button.disabled = isLoading;
    button.classList.toggle('relevant-product-card__button--loading', isLoading);
  }

  showError(message) {
    const errorElement = this.errorElement;
    if (!errorElement) return;

    errorElement.classList.remove('hidden');
    if (this.errorMessage) {
      this.errorMessage.textContent = message;
    }
    this.announce(message);
  }

  hideError() {
    const errorElement = this.errorElement;
    if (!errorElement) return;

    errorElement.classList.add('hidden');
    if (this.errorMessage) {
      this.errorMessage.textContent = '';
    }
  }

  announce(message) {
    const liveRegion = this.liveRegion;
    if (!liveRegion) return;

    liveRegion.textContent = message;
    window.setTimeout(() => {
      liveRegion.textContent = '';
    }, 5000);
  }
}

if (!customElements.get('relevant-product-atc')) {
  customElements.define('relevant-product-atc', RelevantProductAtc);
}
